import { useState } from "react";
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import { convertFormatMomentToLocalTimezone } from "@/lib/api/date";
import { BushelSummaryReportFilters, useDownloadSummaryData } from "@/lib/api/otc";
import { CommonDestinationTimezone } from "@/lib/utils";
import { BearDatePicker } from "../ContractForm";
import { ReportCard, ReportDownloadLink } from "./ReportCard";

/**
 * Summary Report — optional as-of date. Generate downloads a single link.
 */
export function SummaryReportCard() {
  const { toast } = useToast();
  const downloadDataLinkFunc = useDownloadSummaryData();
  const [filters, setFilters] = useState<BushelSummaryReportFilters>({
    effective_as_of: convertFormatMomentToLocalTimezone(
      moment.tz(CommonDestinationTimezone).subtract(1, "day"),
    ),
  });
  const [link, setLink] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const onGenerate = async () => {
    if (isDownloading) return;
    // Clear any prior result for this report before running again.
    setLink(undefined);
    setIsDownloading(true);

    const result = await downloadDataLinkFunc(filters);

    if (!result.success) {
      toast({ title: "Failed to generate", description: "" });
    } else {
      setLink(result.data.link);
    }

    setIsDownloading(false);
  };

  return (
    <ReportCard
      title="Summary Report"
      description="Generate a summary report across all OTC contracts."
      isDownloading={isDownloading}
      onGenerate={onGenerate}
      hasResults={!!link}
      linkContent={link ? <ReportDownloadLink href={link} label="Download Report" /> : null}
    >
      <div className="grid grid-cols-1 gap-3">
        <div className="grid gap-1">
          <BearDatePicker
            tabIndex={0}
            name="summary_report_as_of"
            label="As Of Date"
            value={filters.as_of}
            onValueChange={(date: Date | undefined) =>
              setFilters((previous: BushelSummaryReportFilters) => ({
                ...previous,
                as_of: date,
                effective_as_of: date ? undefined : previous.effective_as_of,
              }))
            }
          />
          <p className="text-sm text-muted-foreground">
                                                         Includes latest version of contract as they were before the end of the calendar "As Of Date". (Backdating Ignored)
          </p>
        </div>

        <div className="text-center text-sm font-semibold text-muted-foreground">OR</div>

        <div className="grid gap-1">
          <BearDatePicker
            tabIndex={1}
            name="summary_report_effective_as_of"
            label="Effective As Of Date"
            value={filters.effective_as_of}
            onValueChange={(date: Date | undefined) =>
              setFilters((previous: BushelSummaryReportFilters) => ({
                ...previous,
                effective_as_of: date,
                as_of: date ? undefined : previous.as_of,
              }))
            }
          />
          <p className="text-sm text-muted-foreground">
                                                         Includes latest version of contracts relative to their Contract or Amendment Dates (Backdating Accepted)
          </p>
        </div>

        <p className="text-sm font-medium text-muted-foreground">
          Select only one date. Setting either date clears the other.
        </p>
      </div>
    </ReportCard>
  );
}
